/**
 * ExportOperation store — backed by the QMS backend (`/ap/{ap_id}/exporter/operations`).
 *
 * Mutations (`add`, `setReply`, `remind`) round-trip to the backend so the
 * `email` channel actually sends a real Resend email. State derivation
 * (`effectiveState`, `recordCurrentState`, etc.) is unchanged from the
 * Phase A implementation.
 *
 * Consumers continue to read from `useExportOperationsStore((s) => s.operations)`
 * — selectors stayed identical so VersandCockpit / ApStatusTile / RecordRow
 * didn't need refactors. Polling lives in `useExportOperationsSync(apId)`.
 */
import { useQuery } from "@tanstack/react-query"
import { useEffect } from "react"
import { create } from "zustand"
import type {
  ExportOperationCreateIn,
  ExportOperationOut,
  ExportOperationReplyIn,
  ResponseAttachmentOut,
} from "../../../../api/client"
import { api } from "../../../../api/client"

export type ExportChannel = "download" | "email"

export type ExportOpState =
  | "draft" // composed but not yet sent — frontend-only, never persisted (✏️)
  | "waiting" // email sent, follow-up date in the future (⏳)
  | "due" // follow-up date reached, no reply yet (🔴) — derived
  | "answered_received" // reply landed via webhook, awaiting user judgement (📨)
  | "answered_ok" // reply received + marked correct (✅)
  | "answered_followup" // reply received + marked incomplete (⚠)
  | "closed" // PDF download — terminal audit entry (📥)

/** Local-only id prefix for draft ops. Anything starting with this is never
 *  round-tripped to the backend — it lives in the operations array purely so
 *  the cockpit timeline renders it. */
export const DRAFT_ID_PREFIX = "draft-"

export function isDraftOp(op: { id: string }): boolean {
  return op.id.startsWith(DRAFT_ID_PREFIX)
}

export interface ExportOperation {
  id: string
  ap_id: string
  record_id: string
  template_ids: string[]
  channel: ExportChannel
  created_at: string
  created_by: string
  state: ExportOpState

  // email-only fields
  email_to?: string
  email_subject?: string
  email_body?: string
  due_at?: string
  response_received_at?: string
  response_summary?: string
  response_file_name?: string
  response_file_content_type?: string
  has_response_attachment?: boolean
  response_attachments?: ResponseAttachmentOut[]
  response_from?: string
  response_subject?: string
  response_body_text?: string
  response_body_html?: string
  provider_message_id?: string
  is_mock?: boolean

  follow_up_of?: string
}

function fromApi(out: ExportOperationOut): ExportOperation {
  return {
    id: out.id,
    ap_id: out.ap_id,
    record_id: out.record_id,
    template_ids: out.template_ids ?? [],
    channel: out.channel,
    state: out.state as ExportOpState,
    created_at: out.created_at,
    created_by: out.created_by,
    email_to: out.email_to ?? undefined,
    email_subject: out.email_subject ?? undefined,
    email_body: out.email_body ?? undefined,
    due_at: out.due_at ?? undefined,
    response_received_at: out.response_received_at ?? undefined,
    response_summary: out.response_summary ?? undefined,
    response_file_name: out.response_file_name ?? undefined,
    response_file_content_type: out.response_file_content_type ?? undefined,
    has_response_attachment: out.has_response_attachment ?? false,
    response_attachments: out.response_attachments ?? [],
    response_from: out.response_from ?? undefined,
    response_subject: out.response_subject ?? undefined,
    response_body_text: out.response_body_text ?? undefined,
    response_body_html: out.response_body_html ?? undefined,
    provider_message_id: out.provider_message_id ?? undefined,
    is_mock: out.is_mock ?? false,
    follow_up_of: out.follow_up_of ?? undefined,
  }
}

function mergeOp(
  prev: ExportOperation[],
  next: ExportOperation,
): ExportOperation[] {
  const idx = prev.findIndex((o) => o.id === next.id)
  if (idx < 0) return [...prev, next]
  const out = [...prev]
  out[idx] = next
  return out
}

function mergeMany(
  prev: ExportOperation[],
  fresh: ExportOperation[],
  apId: string,
): ExportOperation[] {
  // Replace all ops for this AP with the freshly-fetched set; keep ops from
  // other APs intact (the cockpit shows one AP at a time but selectors look
  // across all ops). Local drafts (DRAFT_ID_PREFIX) are preserved across
  // hydrations — the backend doesn't know about them.
  const otherApOps = prev.filter((o) => o.ap_id !== apId)
  const localDrafts = prev.filter((o) => o.ap_id === apId && isDraftOp(o))
  return [...otherApOps, ...fresh, ...localDrafts]
}

interface ExportOpsState {
  operations: ExportOperation[]
  /**
   * Demo-only: while a mocked inbound reply is being "evaluated" before the
   * state flips to answered_received, the cockpit shows an animated banner
   * over the matching waiting op. The id is the op currently being detected.
   */
  replyDetectionOpId: string | null
  setReplyDetectionOpId: (id: string | null) => void
  hydrate: (apId: string) => Promise<void>
  add: (
    input: ExportOperationCreateIn & { ap_id: string },
  ) => Promise<ExportOperation>
  setReply: (
    apId: string,
    id: string,
    payload:
      | { kind: "ok" }
      | { kind: "followup"; reason: string; fileName?: string },
  ) => Promise<void>
  remind: (apId: string, id: string) => Promise<void>
  /** Demo-mode: synthesise an inbound reply (incl. mock attachment). */
  simulateReply: (
    apId: string,
    id: string,
    recordName?: string,
  ) => Promise<void>
  /**
   * Hand the inbound reply's document off to an importer AP. Closes the
   * operation as ``answered_ok`` and creates a PendingDocument bucket the
   * importer surfaces for human confirmation.
   */
  forwardToImporter: (
    apId: string,
    id: string,
    targetApId: string,
  ) => Promise<{ pending_document_id: string; target_record_id: string | null }>
  /** Push a frontend-only draft into the operations array. */
  addDraft: (input: {
    ap_id: string
    record_id: string
    template_ids: string[]
    email_to?: string
    email_subject?: string
    email_body?: string
  }) => ExportOperation
  /** Drop a frontend-only draft (e.g. after the user actually sends it). */
  removeDraft: (id: string) => void
  /** Local-only reset — does not delete server-side operations. */
  clearAll: () => void
}

export const useExportOperationsStore = create<ExportOpsState>((set) => ({
  operations: [],
  replyDetectionOpId: null,
  setReplyDetectionOpId: (id) => set({ replyDetectionOpId: id }),

  hydrate: async (apId) => {
    const list = await api.exporterOperationsList(apId)
    const fresh = list.map(fromApi)
    set((state) => ({ operations: mergeMany(state.operations, fresh, apId) }))
  },

  add: async (input) => {
    const { ap_id, ...body } = input
    const out = await api.exporterOperationsCreate(ap_id, body)
    const op = fromApi(out)
    set((state) => ({ operations: mergeOp(state.operations, op) }))
    return op
  },

  setReply: async (apId, id, payload) => {
    const body: ExportOperationReplyIn =
      payload.kind === "ok"
        ? { kind: "ok" }
        : {
            kind: "followup",
            reason: payload.reason,
            file_name: payload.fileName,
          }
    const out = await api.exporterOperationsReply(apId, id, body)
    const op = fromApi(out)
    set((state) => ({ operations: mergeOp(state.operations, op) }))
  },

  remind: async (apId, id) => {
    const out = await api.exporterOperationsRemind(apId, id)
    const op = fromApi(out)
    set((state) => ({ operations: mergeOp(state.operations, op) }))
  },

  simulateReply: async (apId, id, recordName) => {
    const out = await api.exporterOperationsSimulateReply(apId, id, {
      record_name: recordName,
    })
    const op = fromApi(out)
    set((state) => ({ operations: mergeOp(state.operations, op) }))
  },

  forwardToImporter: async (apId, id, targetApId) => {
    const out = await api.exporterOperationsForwardToImporter(apId, id, {
      target_ap_id: targetApId,
    })
    const op = fromApi(out.operation)
    set((state) => ({ operations: mergeOp(state.operations, op) }))
    return {
      pending_document_id: out.pending_document_id,
      target_record_id: out.target_record_id,
    }
  },

  addDraft: (input) => {
    const id = `${DRAFT_ID_PREFIX}${
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2)
    }`
    const draft: ExportOperation = {
      id,
      ap_id: input.ap_id,
      record_id: input.record_id,
      template_ids: input.template_ids,
      channel: "email",
      state: "draft",
      created_at: new Date().toISOString(),
      created_by: "demo",
      email_to: input.email_to,
      email_subject: input.email_subject,
      email_body: input.email_body,
      is_mock: true,
    }
    set((state) => ({ operations: mergeOp(state.operations, draft) }))
    return draft
  },

  removeDraft: (id) => {
    if (!id.startsWith(DRAFT_ID_PREFIX)) return
    set((state) => ({
      operations: state.operations.filter((o) => o.id !== id),
    }))
  },

  clearAll: () => set({ operations: [] }),
}))

// ── Integration status (real Resend vs. local mock) ─────────────────────────

export interface ExporterIntegrationStatus {
  mode: "real" | "mock"
  inboundDomain: string | null
  fromEmail: string | null
}

export function useExporterIntegrationStatus(): ExporterIntegrationStatus {
  const { data } = useQuery({
    queryKey: ["exporter-integration-status"],
    queryFn: () => api.exporterIntegrationStatus(),
    staleTime: 60_000,
  })
  return {
    mode: data?.mode ?? "mock",
    inboundDomain: data?.inbound_domain ?? null,
    fromEmail: data?.from_email ?? null,
  }
}

// ── Polling hook — call this from any AP shell that wants live updates ──

const POLL_INTERVAL_MS = 15_000

/**
 * Mounts a polling effect that hydrates the store from the backend every
 * POLL_INTERVAL_MS and once on mount. Used to surface inbound replies in the
 * cockpit without a websocket layer (per-design: no real-time notifications
 * yet).
 */
export function useExportOperationsSync(apId: string | null | undefined): void {
  const hydrate = useExportOperationsStore((s) => s.hydrate)
  useEffect(() => {
    if (!apId) return
    let cancelled = false
    const tick = () => {
      hydrate(apId).catch((err) => {
        if (!cancelled) console.warn("[exporter-ops] hydrate failed", err)
      })
    }
    tick()
    const handle = window.setInterval(tick, POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      window.clearInterval(handle)
    }
  }, [apId, hydrate])
}

// ── Derivation helpers (unchanged from Phase A) ──────────────────────────────

export function effectiveState(
  op: ExportOperation,
  now: Date = new Date(),
): ExportOpState {
  if (op.state !== "waiting") return op.state
  if (!op.due_at) return "waiting"
  return new Date(op.due_at) <= now ? "due" : "waiting"
}

export function isOpenState(s: ExportOpState): boolean {
  return (
    s === "draft" ||
    s === "waiting" ||
    s === "due" ||
    s === "answered_received" ||
    s === "answered_followup"
  )
}

export function isClosedState(s: ExportOpState): boolean {
  return s === "answered_ok" || s === "closed"
}

export function addDaysIso(base: Date, days: number): string {
  const d = new Date(base)
  d.setDate(d.getDate() + days)
  return d.toISOString()
}

// ── Selectors ─────────────────────────────────────────────────────────────────

export function operationsForRecord(
  ops: ExportOperation[],
  apId: string,
  recordId: string,
): ExportOperation[] {
  return ops
    .filter((o) => o.ap_id === apId && o.record_id === recordId)
    .sort((a, b) => (b.created_at < a.created_at ? -1 : 1))
}

export function operationsForAp(
  ops: ExportOperation[],
  apId: string,
): ExportOperation[] {
  return ops.filter((o) => o.ap_id === apId)
}

export function dueCountForAp(
  ops: ExportOperation[],
  apId: string,
  now: Date = new Date(),
): number {
  return ops.filter((o) => o.ap_id === apId && effectiveState(o, now) === "due")
    .length
}

/**
 * Returns the *current* state of a record — the effective state of its latest
 * operation. A record represents one ongoing thread at a time; older
 * operations are history, not concurrent state. Returns null if no operation
 * has ever been recorded for the record.
 */
export function recordCurrentState(
  ops: ExportOperation[],
  apId: string,
  recordId: string,
  now: Date = new Date(),
): { state: ExportOpState; op: ExportOperation } | null {
  const cases = operationsForRecord(ops, apId, recordId)
  if (cases.length === 0) return null
  const latest = cases[0] // operationsForRecord sorts newest-first
  return { state: effectiveState(latest, now), op: latest }
}

export interface ApStateBreakdown {
  due: number
  waiting: number
  followUp: number
  ok: number
  /** Records with a download-only audit entry as their latest op. */
  download: number
  /** Inbound reply received, user judgement pending. */
  received: number
  /** Records with no operation at all. */
  none: number
}

/**
 * Counts records (not operations) by their *current* state for a given AP and
 * the supplied record-id set. Each record contributes to exactly one bucket.
 */
export function summarizeRecordsForAp(
  ops: ExportOperation[],
  apId: string,
  recordIds: string[],
  now: Date = new Date(),
): ApStateBreakdown {
  const breakdown: ApStateBreakdown = {
    due: 0,
    waiting: 0,
    followUp: 0,
    ok: 0,
    download: 0,
    received: 0,
    none: 0,
  }
  for (const id of recordIds) {
    const cur = recordCurrentState(ops, apId, id, now)
    if (!cur) {
      breakdown.none += 1
      continue
    }
    switch (cur.state) {
      case "due":
        breakdown.due += 1
        break
      case "waiting":
        breakdown.waiting += 1
        break
      case "answered_received":
        breakdown.received += 1
        break
      case "answered_followup":
        breakdown.followUp += 1
        break
      case "answered_ok":
        breakdown.ok += 1
        break
      case "closed":
        breakdown.download += 1
        break
    }
  }
  return breakdown
}
